export default function pricingTariffDescriptions() {
  const descriptions = [...document.querySelectorAll('.tariffs--pricing .js-tariffs__description')];
  if (!descriptions.length) return () => {};

  const mobile = window.matchMedia('(max-width: 900px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const states = descriptions.map((description) => {
    const card = description.closest('.tariffs__card');
    const state = {
      description,
      card,
      viewport: description.querySelector('.tariffs__description-viewport'),
      mask: description.querySelector('.tariffs__description-mask'),
      content: description.querySelector('.tariffs__card-description'),
      fade: description.querySelector('.tariffs__description-fade'),
      actions: description.querySelector('.tariffs__description-actions'),
      toggle: description.querySelector('.tariffs__description-toggle'),
      surface: card.querySelector('.tariffs__card-surface'),
      buyButton: card.querySelector('.tariffs__buy-button'),
      expanded: false,
      collapsible: false,
      fullHeight: 0,
      previewHeight: 0,
      finish: null,
    };

    state.toggle.addEventListener('click', () => setExpanded(state, !state.expanded));
    // Keyboard focus on a rich-text link must never remain in the clipped part.
    state.content.addEventListener('focusin', () => {
      if (!state.expanded) setExpanded(state, true);
    });
    return state;
  });

  function applyState(state) {
    const expanded = !state.collapsible || state.expanded;
    state.description.classList.toggle('tariffs__description--collapsible', state.collapsible);
    state.description.classList.toggle('tariffs__description--expanded', state.collapsible && expanded);
    state.toggle.hidden = !state.collapsible;
    state.toggle.setAttribute('aria-expanded', String(expanded));
    state.toggle.querySelector('[data-description-expand]').hidden = expanded;
    state.toggle.querySelector('[data-description-collapse]').hidden = !expanded;

    if (state.collapsible) {
      state.viewport.style.height = `${expanded ? state.fullHeight : state.previewHeight}px`;
    } else {
      state.viewport.style.removeProperty('height');
    }
  }

  function refresh() {
    states.forEach((state) => state.finish?.());
    // Read natural content sizes together, including a panel just made visible.
    const sizes = states.map((state) => ({
      visible: state.content.getClientRects().length > 0,
      fullHeight: state.content.offsetHeight,
      previewHeight: parseFloat(getComputedStyle(state.description)
        .getPropertyValue('--tariffs-description-preview-height')) || 0,
    }));

    states.forEach((state, index) => {
      const size = sizes[index];
      if (!size.visible) return;
      state.fullHeight = size.fullHeight;
      state.previewHeight = size.previewHeight;
      state.collapsible = mobile.matches && size.fullHeight > size.previewHeight + 1;
      applyState(state);
    });
  }

  function setExpanded(state, expanded) {
    if (!state.collapsible || !mobile.matches || state.expanded === expanded) return;
    state.finish?.();

    if (reducedMotion.matches || !state.mask.animate) {
      state.expanded = expanded;
      applyState(state);
      return;
    }

    const movingElements = [state.actions, state.buyButton, state.fade];
    const oldPositions = movingElements.map((element) => element.getBoundingClientRect().top);
    const oldHeight = state.viewport.offsetHeight;
    const oldCardHeight = state.card.offsetHeight;
    const oldSurfaceHeight = state.surface.getBoundingClientRect().height;

    // Commit the final layout once; only transforms/opacity change during motion.
    state.expanded = expanded;
    applyState(state);

    const newPositions = movingElements.map((element) => element.getBoundingClientRect().top);
    const newHeight = state.viewport.offsetHeight;
    const newCardHeight = state.card.offsetHeight;
    const newSurfaceHeight = state.surface.getBoundingClientRect().height;
    const maskHeight = Math.max(oldHeight, newHeight);
    const oldOffset = oldHeight - maskHeight;
    const newOffset = newHeight - maskHeight;
    const options = { duration: 280, easing: 'cubic-bezier(0.2, 0, 0, 1)', fill: 'both' };

    // Keep the carousel tall enough while a card is visually collapsing.
    if (oldCardHeight > newCardHeight) state.card.style.minHeight = `${oldCardHeight}px`;
    state.mask.style.height = `${maskHeight}px`;
    state.surface.style.height = `${newSurfaceHeight}px`;
    state.description.classList.add('tariffs__description--animating');
    state.card.classList.add('tariffs__card--description-animating');

    const animations = [
      // Move the clipping window and counter-translate its text by the same amount.
      // This reveals content without scaling text or animating the clip's height.
      state.mask.animate([
        { transform: `translateY(${oldOffset}px)` },
        { transform: `translateY(${newOffset}px)` },
      ], options),
      state.content.animate([
        { transform: `translateY(${-oldOffset}px)` },
        { transform: `translateY(${-newOffset}px)` },
      ], options),
      state.surface.animate([
        { transform: `scaleY(${oldSurfaceHeight / newSurfaceHeight})` },
        { transform: 'scaleY(1)' },
      ], options),
      ...movingElements.map((element, index) => element.animate([
        {
          transform: `translateY(${oldPositions[index] - newPositions[index]}px)`,
          ...(element === state.fade ? { opacity: expanded ? 1 : 0 } : {}),
        },
        {
          transform: 'translateY(0)',
          ...(element === state.fade ? { opacity: expanded ? 0 : 1 } : {}),
        },
      ], options)),
    ];

    let finished = false;
    function finish() {
      if (finished) return;
      finished = true;
      animations.forEach((animation) => animation.cancel());
      state.mask.style.removeProperty('height');
      state.surface.style.removeProperty('height');
      state.card.style.removeProperty('min-height');
      state.description.classList.remove('tariffs__description--animating');
      state.card.classList.remove('tariffs__card--description-animating');
      state.finish = null;
    }
    state.finish = finish;
    Promise.all(animations.map((animation) => animation.finished)).then(finish, finish);
  }

  mobile.addEventListener('change', refresh);
  reducedMotion.addEventListener('change', () => states.forEach((state) => state.finish?.()));
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(refresh);
    states.forEach((state) => observer.observe(state.content));
  } else {
    window.addEventListener('resize', refresh);
  }
  refresh();
  return refresh;
}
